import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useToast } from '../../hooks/use-toast';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [clientCredentials, setClientCredentials] = useState({ email: '', password: '' });
  const [engineerCredentials, setEngineerCredentials] = useState({ email: '', password: '' });

  const handleClientLogin = async (e) => {
    e.preventDefault();
    try {
      const { authAPI } = await import('../../api');
      const data = await authAPI.login({
        email: clientCredentials.email,
        password: clientCredentials.password,
        role: 'client'
      });
      
      localStorage.setItem('token', data.token);
      onLogin('client', data.user);
      toast({
        title: "Connexion réussie",
        description: "Bienvenue sur votre espace client !",
      });
      navigate('/client/dashboard');
    } catch (error) {
      toast({
        title: "Erreur de connexion",
        description: error.response?.data?.detail || "Identifiants invalides",
        variant: "destructive"
      });
    }
  };

  const handleEngineerLogin = async (e) => {
    e.preventDefault();
    try {
      const { authAPI } = await import('../../api');
      const data = await authAPI.login({
        email: engineerCredentials.email,
        password: engineerCredentials.password,
        role: 'engineer'
      });
      
      localStorage.setItem('token', data.token);
      onLogin('engineer', data.user);
      toast({
        title: "Connexion réussie",
        description: "Bienvenue sur votre espace ingénieur !",
      });
      navigate('/engineer/dashboard');
    } catch (error) {
      toast({
        title: "Erreur de connexion",
        description: error.response?.data?.detail || "Identifiants invalides",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center px-6 py-32">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2">
            <span className="text-[#A6A6A6]">GT</span>
            <span className="text-[#C00000]"> ENGINEERING</span>
          </h1>
          <p className="text-[#A6A6A6]">Engineered Performance.</p>
        </div>

        <Tabs defaultValue="client" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-[#1a1a1a] mb-6">
            <TabsTrigger value="client" className="data-[state=active]:bg-[#C00000] data-[state=active]:text-white">
              Client
            </TabsTrigger>
            <TabsTrigger value="engineer" className="data-[state=active]:bg-[#C00000] data-[state=active]:text-white">
              Ingénieur
            </TabsTrigger>
          </TabsList>

          <TabsContent value="client">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white">Connexion Client</CardTitle>
                <CardDescription className="text-[#A6A6A6]">
                  Accédez à votre espace client
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleClientLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="client-email" className="text-[#A6A6A6]">Email</Label>
                    <Input
                      id="client-email"
                      type="email"
                      placeholder="votre@email.com"
                      value={clientCredentials.email}
                      onChange={(e) => setClientCredentials({ ...clientCredentials, email: e.target.value })}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client-password" className="text-[#A6A6A6]">Mot de passe</Label>
                    <Input
                      id="client-password"
                      type="password"
                      placeholder="••••••••"
                      value={clientCredentials.password}
                      onChange={(e) => setClientCredentials({ ...clientCredentials, password: e.target.value })}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full bg-[#C00000] hover:bg-[#C00000]/90 text-white font-semibold">
                    Se connecter
                  </Button>
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="text-[#A6A6A6] hover:text-[#C00000] text-sm transition-colors"
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="engineer">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white">Connexion Ingénieur</CardTitle>
                <CardDescription className="text-[#A6A6A6]">
                  Accédez à votre espace ingénieur
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleEngineerLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="engineer-email" className="text-[#A6A6A6]">Email</Label>
                    <Input
                      id="engineer-email"
                      type="email"
                      placeholder="ingenieur@gt-engineering.com"
                      value={engineerCredentials.email}
                      onChange={(e) => setEngineerCredentials({ ...engineerCredentials, email: e.target.value })}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="engineer-password" className="text-[#A6A6A6]">Mot de passe</Label>
                    <Input
                      id="engineer-password"
                      type="password"
                      placeholder="••••••••"
                      value={engineerCredentials.password}
                      onChange={(e) => setEngineerCredentials({ ...engineerCredentials, password: e.target.value })}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full bg-[#C00000] hover:bg-[#C00000]/90 text-white font-semibold">
                    Se connecter
                  </Button>
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="text-[#A6A6A6] hover:text-[#C00000] text-sm transition-colors"
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="text-center mt-6">
          <p className="text-[#A6A6A6] text-sm">
            Pas encore de compte ?{' '}
            <button
              onClick={() => navigate('/register')}
              className="text-[#C00000] hover:underline font-semibold"
            >
              S'inscrire
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
